import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import FormError from "@/components/common/FormError";
import { ShieldCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const digits = (s) => s.replace(/\D/g, "").slice(0, 6);

/* Verify / resend logic for the email-OTP step; keeps the current mfa token as it rotates on resend. */
function useMfaVerification(mfa, { onVerified, onBack }) {
  const { verifyMfa, resendMfa } = useAuth();
  const [token, setToken] = useState(mfa.mfaToken);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState(mfa.emailSent === false ? "We couldn't deliver the code email. Use 'Resend code' to try again." : "");

  const verify = async (code, trust) => {
    setBusy(true);
    setError("");
    const res = await verifyMfa(token, code, trust);
    setBusy(false);
    if (res.ok) { onVerified(res.user); return; }
    setError(res.error);
    if (res.status === 401 && /expired|session/i.test(res.error || "")) onBack();
  };

  const resend = async () => {
    setResending(true);
    const res = await resendMfa(token);
    setResending(false);
    if (!res.ok) { setError(res.error); return; }
    setToken(res.mfaToken);
    setError("");
    toast.success(res.emailSent ? "A new code has been sent" : "Code regenerated, but email delivery failed");
  };

  return { busy, resending, error, clearError: () => setError(""), verify, resend };
}

export default function MfaStep({ mfa, onVerified, onBack }) {
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const m = useMfaVerification(mfa, { onVerified, onBack });
  const ready = code.length === 6;

  const submit = (e) => {
    e.preventDefault();
    if (!m.busy && ready) m.verify(code, trust);
  };

  return (
    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="mfa-form">
      <div className="flex items-start gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-900">
        <ShieldCheck className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">Two-step verification</div>
          <div className="text-xs mt-0.5">We emailed a 6-digit code to <span className="font-mono" data-testid="mfa-masked-email">{mfa.maskedEmail}</span>. It expires in 10 minutes.</div>
        </div>
      </div>
      <div>
        <Label htmlFor="mfa-code">Verification code</Label>
        <Input id="mfa-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} required value={code}
          onChange={(e) => { setCode(digits(e.target.value)); m.clearError(); }}
          placeholder="123456" className="mt-1.5 text-center text-2xl tracking-[0.5em] font-mono h-14" data-testid="mfa-code-input" autoFocus />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none">
        <Checkbox checked={trust} onCheckedChange={(v) => setTrust(Boolean(v))} data-testid="mfa-trust-device" />
        Trust this device for 30 days
      </label>
      <FormError message={m.error} testId="login-error" />
      <Button type="submit" disabled={m.busy || !ready} aria-busy={m.busy} className="w-full bg-slate-950 hover:bg-slate-800 text-white h-11" data-testid="mfa-verify-button">
        {m.busy ? (<><Loader2 className="h-4 w-4 animate-spin mr-2" /> Verifying…</>) : "Verify & sign in"}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900" data-testid="mfa-back">
          <ArrowLeft className="h-3 w-3" /> Back to password
        </button>
        <button type="button" onClick={m.resend} disabled={m.resending} className="text-blue-600 hover:underline disabled:opacity-50" data-testid="mfa-resend">
          {m.resending ? "Sending…" : "Resend code"}
        </button>
      </div>
    </form>
  );
}
